import React, { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { Plus, Pencil, Trash2, Loader2, BookOpen, Users, Globe, Layers } from "lucide-react";
import { Button } from "../../components/ui/button";
import { fmtCurrency, parseMoney } from "../../lib/display-labels";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { PageHeaderBand } from "../../components/layout/PageHeaderBand";
import { LoadFailureNotice } from "../../components/ui/LoadFailureNotice";
import { LOAD_FAILURE_QUERY_OPTIONS, queryFailureError, queryLoadFailure } from "../../lib/load-failure";
import { unwrapArray } from "../../lib/api-envelope";
import { calendarDayString, formatCalendarDate } from "../../lib/calendar-date";
import { displayValue } from "../../features/fieldiq-handoff/contracts";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "../../components/ui/select";
import { toast } from "sonner";
import { api, getApiErrorMessage } from "../../api/client";
import { useBillHaulHourly } from "../../hooks/useBillHaulHourly";
import { HAUL_RATE_TYPE } from "../../types/rental-haul";

/* ------------------------------------------------------------------ */
/*  Types & Constants                                                  */
/* ------------------------------------------------------------------ */

/**
 * A price book row as GET /surface-rentals/price-books sends it: `pb.*` plus
 * the joined `customer_name`. The rate column is `rate`, qualified by
 * `rate_type` (daily / hourly / flat / standby). It is NOT a daily rate: this
 * page used to read a `daily_rate` key the API has never sent, so every row
 * showed a dash, and relabelling `rate` as "Daily Rate" would print an hourly
 * or flat figure as a per-day one. The unit comes from `rate_type`.
 */
interface PriceBookEntry {
  id: string;
  asset_type: string | null;
  item_name: string | null;
  /** null is a rate for every customer, not a missing customer. */
  customer_id: string | null;
  customer_name: string | null;
  rate: number | null;
  rate_type: string | null;
  standby_rate: number | null;
  /** `mob_fee` / `demob_fee` (api#953). Null is NO fee; a row's fee overrides the asset's. */
  mob_fee: number | null;
  demob_fee: number | null;
  billing_trigger: string | null;
  minimum_days: number | null;
  currency: string | null;
  valid_from: string | null;
  valid_to: string | null;
  notes: string | null;
  /** An hourly haul rate's truck (api#1054); null is any truck. Only on `haul_hourly` rows. */
  fleet_unit_id: string | null;
}

interface Customer {
  id: string;
  name: string;
}

interface PriceBookForm {
  customer_id: string;
  asset_type: string;
  item_name: string;
  rate: string;
  standby_rate: string;
  mob_fee: string;
  demob_fee: string;
  billing_trigger: string;
  minimum_days: string;
  currency: string;
  valid_from: string;
  valid_to: string;
  notes: string;
  /** An hourly haul rate (rate_type 'haul_hourly', api#1054) instead of an equipment rate. */
  haul: boolean;
  /** The truck a haul rate is for; "" is any truck. */
  fleet_unit_id: string;
}

const ASSET_TYPES = [
  "Storage Tanks",
  "Separators",
  "Generators",
  "Dehydration",
  "Line Heaters",
  "Environmental",
  "Pumps",
  "Other",
] as const;

/** Exactly `price_books_billing_trigger_check`. `on_arrival` was offered here and is a 500. */
const BILLING_TRIGGERS = [
  { value: "arrival", label: "On Arrival" },
  { value: "rig_release", label: "Rig Release" },
  { value: "spud", label: "Spud" },
  { value: "custom", label: "Custom" },
] as const;

const CURRENCIES = ["CAD", "USD"] as const;

function blankForm(haul = false): PriceBookForm {
  return {
    haul,
    fleet_unit_id: "",
    customer_id: "",
    asset_type: "",
    item_name: "",
    rate: "",
    standby_rate: "",
    mob_fee: "",
    demob_fee: "",
    billing_trigger: "arrival",
    minimum_days: "1",
    currency: "CAD",
    valid_from: "",
    valid_to: "",
    notes: "",
  };
}

/**
 * The billing trigger as the API stores it. Exhaustive over
 * `price_books_billing_trigger_check`. An unrecognised value renders a slate dash,
 * never a guessed label. Outline chips, not status fills: a trigger is a
 * property of the rate, not the row's state, so it claims no status colour.
 */
/** A `valid_from` / `valid_to` calendar day as a date input wants it. */
const dayOf = (v: string | null): string => calendarDayString(v) ?? "";

/** The rate's unit comes from `rate_type`; a new rate is created daily (the API's default). */
function rateLabel(rateType: string | null): string {
  switch (rateType) {
    case "daily": return "Rate per day ($)";
    case "hourly": return "Rate per hour ($)";
    case "flat": return "Flat rate ($)";
    case "standby": return "Standby rate per day ($)";
    case HAUL_RATE_TYPE: return "Haul rate per hour ($)";
    default: return "Rate ($)";
  }
}

/**
 * The form as `POST` / `PATCH /surface-rentals/price-books` read it (#815).
 * The keys are `rate` and `minimum_days`: the old body sent `daily_rate`
 * (every create a 400, every edit left the rate unchanged) and `min_days`
 * (never read). A blank optional field is OMITTED: standby used to go out as
 * `0`, a real rate of nothing.
 *
 * On an edit, a field that HAD a value and is now blank is sent as `null`,
 * which clears it (api#975: absent keeps, a value sets, null clears). This used
 * to be refused here, when PATCH kept the old value whatever was sent. A field
 * the API will not clear (the customer, among others) answers 400
 * FIELD_NOT_CLEARABLE, shown as sent.
 *
 * Mob and demob fees (api#953): blank is no fee, a typed 0 is a $0 fee.
 */
function priceBookBody(
  form: PriceBookForm,
  editing: PriceBookEntry | null,
): { body: Record<string, unknown> } | { error: string } {
  if (form.haul) return haulRateBody(form, editing);
  if (!form.asset_type || !form.item_name.trim() || !form.rate.trim()) {
    return { error: "Please fill in required fields (Asset Type, Item Name, Rate)" };
  }
  const rate = Number(form.rate);
  // A typed 0 is a real $0 day rate and the resolver bills it as $0 (api#1063).
  // The haul hourly rate below keeps its "more than $0" guard: api#1063 covers
  // the rental-period resolver only.
  if (!Number.isFinite(rate) || rate < 0) return { error: "Rate can't be negative." };
  if (!form.billing_trigger) return { error: "Choose a billing trigger." };

  const body: Record<string, unknown> = {
    asset_type: form.asset_type,
    item_name: form.item_name.trim(),
    rate,
    billing_trigger: form.billing_trigger,
    currency: form.currency,
  };

  if (form.customer_id) body.customer_id = form.customer_id;
  else if (editing?.customer_id) body.customer_id = null;

  if (form.standby_rate.trim() !== "") {
    const standby = Number(form.standby_rate);
    if (!Number.isFinite(standby) || standby < 0) return { error: "Standby rate can't be negative." };
    body.standby_rate = standby;
  } else if (editing?.standby_rate != null) body.standby_rate = null;

  for (const [key, label] of [["mob_fee", "Mob fee"], ["demob_fee", "Demob fee"]] as const) {
    const text = form[key].trim();
    if (text !== "") {
      const n = Number(text);
      if (!Number.isFinite(n)) return { error: `${label} must be an amount, or left blank for no fee.` };
      body[key] = n;
    } else if (editing?.[key] != null) body[key] = null;
  }

  if (form.minimum_days.trim() !== "") {
    const days = Number(form.minimum_days);
    if (!Number.isInteger(days) || days < 1) return { error: "Minimum days must be a whole number, 1 or more." };
    body.minimum_days = days;
  } else if (editing?.minimum_days != null) body.minimum_days = null;

  if (form.valid_from) body.valid_from = form.valid_from;
  else if (editing?.valid_from) body.valid_from = null;
  if (form.valid_to) body.valid_to = form.valid_to;
  else if (editing?.valid_to) body.valid_to = null;
  if (form.valid_from && form.valid_to && form.valid_to < form.valid_from) {
    return { error: "Valid To is before Valid From." };
  }

  if (form.notes.trim() !== "") body.notes = form.notes.trim();
  else if (editing?.notes) body.notes = null;

  return { body };
}

/**
 * An hourly haul rate (rentals-haul item 5, api#1054): a price book row with
 * rate_type 'haul_hourly', for one customer or the default book, and for one
 * truck or any. The equipment fields (asset type, standby, fees, trigger,
 * minimum days) do not apply and are not sent. A trailer is refused by the API
 * (422 HAUL_RATE_UNIT_IS_TRAILER), shown as sent.
 */
function haulRateBody(
  form: PriceBookForm,
  editing: PriceBookEntry | null,
): { body: Record<string, unknown> } | { error: string } {
  if (!form.item_name.trim() || !form.rate.trim()) return { error: "Please fill in Item Name and Rate." };
  const rate = Number(form.rate);
  if (!Number.isFinite(rate) || rate <= 0) return { error: "Rate must be more than $0." };
  const body: Record<string, unknown> = { item_name: form.item_name.trim(), rate, currency: form.currency };
  if (!editing) body.rate_type = HAUL_RATE_TYPE;
  if (form.customer_id) body.customer_id = form.customer_id;
  else if (editing?.customer_id) body.customer_id = null;
  if (form.fleet_unit_id) body.fleet_unit_id = form.fleet_unit_id;
  else if (editing?.fleet_unit_id) body.fleet_unit_id = null;
  if (form.valid_from) body.valid_from = form.valid_from;
  else if (editing?.valid_from) body.valid_from = null;
  if (form.valid_to) body.valid_to = form.valid_to;
  else if (editing?.valid_to) body.valid_to = null;
  if (form.valid_from && form.valid_to && form.valid_to < form.valid_from) return { error: "Valid To is before Valid From." };
  if (form.notes.trim() !== "") body.notes = form.notes.trim();
  else if (editing?.notes) body.notes = null;
  return { body };
}

const TRIGGER_LABEL: Record<string, string> = {
  arrival: "On arrival",
  rig_release: "Rig release",
  spud: "Spud",
  custom: "Custom",
};

/** Exhaustive over `price_books_rate_type_check`. */
const RATE_UNIT: Record<string, string> = {
  daily: "/ day",
  hourly: "/ hr",
  flat: "flat",
  standby: "standby",
  [HAUL_RATE_TYPE]: "/ hr haul",
};

const str = (v: unknown): string | null => (v === null || v === undefined || v === "" ? null : String(v));
const int = (v: unknown): number | null => {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

function toEntry(r: Record<string, unknown>): PriceBookEntry {
  return {
    id: String(r.id ?? ""),
    asset_type: str(r.asset_type),
    item_name: str(r.item_name),
    customer_id: str(r.customer_id),
    customer_name: str(r.customer_name),
    rate: parseMoney(r.rate),
    rate_type: str(r.rate_type),
    standby_rate: parseMoney(r.standby_rate),
    mob_fee: parseMoney(r.mob_fee),
    demob_fee: parseMoney(r.demob_fee),
    billing_trigger: str(r.billing_trigger),
    minimum_days: int(r.minimum_days),
    currency: str(r.currency),
    valid_from: str(r.valid_from),
    valid_to: str(r.valid_to),
    notes: str(r.notes),
    fleet_unit_id: str(r.fleet_unit_id),
  };
}

const fmtDay = (d: string | null) => formatCalendarDate(d, { month: "short", day: "numeric", year: "numeric" }) ?? "—";

/** A null customer_id is the default rate for every customer. A customer id whose name did not join is a gap. */
const customerLabel = (e: PriceBookEntry) => (e.customer_id == null ? "All customers" : e.customer_name ?? "—");

function RateCell({ e }: { e: PriceBookEntry }) {
  if (e.rate == null) return <>—</>;
  const unit = e.rate_type ? RATE_UNIT[e.rate_type] : undefined;
  return (
    <>
      {fmtCurrency(e.rate, e.currency)} <span className="text-xs font-bold text-fieldiq-muted">{unit ?? "—"}</span>
    </>
  );
}

function TriggerChip({ value }: { value: string | null }) {
  const label = value ? TRIGGER_LABEL[value] : undefined;
  return (
    <span className="inline-flex whitespace-nowrap rounded-fi-pill border border-fieldiq-border px-2.5 py-1 text-[0.68rem] font-bold uppercase tracking-[0.06em] leading-none text-fieldiq-text">
      {label ?? "—"}
    </span>
  );
}

type ScopeKey = "all" | "customer" | "default";

/**
 * Scope chips. The counts are tallies of the loaded list, which is honest here
 * because GET /price-books is unpaginated and no endpoint returns the figures.
 * The list is only CURRENT rates: the API drops any row whose valid_to has passed.
 */
const SCOPES: { key: ScopeKey; label: string; match: (e: PriceBookEntry) => boolean }[] = [
  { key: "all", label: "All", match: () => true },
  { key: "customer", label: "Customer rates", match: (e) => e.customer_id != null },
  { key: "default", label: "Default rates", match: (e) => e.customer_id == null },
];

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

const PriceBooks = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // The price book is the register. Customers feed the dialog and the customer
  // filter only, so they are their own query: a customers failure must not read
  // as "no rates", and a rates failure must not hide behind a customer list.
  const priceBooksQuery = useQuery({
    queryKey: ["rentals", "priceBooks", "all"],
    queryFn: () =>
      api.rentals.priceBooks.list().then((r) => unwrapArray<Record<string, unknown>>(r.data).map(toEntry)),
    staleTime: 30_000,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
  const customersQuery = useQuery({
    queryKey: ["rentals", "priceBooks", "customers"],
    queryFn: () =>
      api.customers.list().then((r) =>
        unwrapArray<Record<string, unknown>>(r.data).map(
          (c): Customer => ({ id: String(c.id ?? ""), name: String(c.name ?? c.company_name ?? "") }),
        ),
      ),
    staleTime: 30_000,
  });

  const failed = queryLoadFailure(priceBooksQuery) != null;
  const loading = priceBooksQuery.isLoading && !failed;
  const entries = priceBooksQuery.data;
  const customers = customersQuery.data ?? [];

  // Filters
  const [scope, setScope] = useState<ScopeKey>("all");
  const [customerFilter, setCustomerFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");

  // Modal
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  /** The row being edited, as loaded: what a cleared field would have cleared. */
  const [editingEntry, setEditingEntry] = useState<PriceBookEntry | null>(null);
  const [form, setForm] = useState<PriceBookForm>(blankForm());

  // Hourly haul rates (api#1054): offered when the company bills hauls by the
  // hour; a row's truck is named from GET /trucking/fleet?type=truck.
  const { on: billHaul } = useBillHaulHourly();
  const hasHaulRows = (entries ?? []).some((e) => e.rate_type === HAUL_RATE_TYPE);
  const trucksQuery = useQuery({
    queryKey: ["trucking-fleet", "trucks", "with-retired"],
    queryFn: () =>
      api.trucking.fleet
        .list({ type: "truck", include_retired: "true" })
        .then((r) => unwrapArray<{ id: string; unit_number?: string | null; status?: string | null }>(r.data)),
    enabled: hasHaulRows || (modalOpen && form.haul),
    staleTime: 60_000,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
  const trucksFailed = queryLoadFailure(trucksQuery) != null;
  const activeTrucks = (trucksQuery.data ?? []).filter((t) => t.status !== "retired");
  const truckLabel = (id: string | null) => {
    if (!id) return "Any truck";
    const t = (trucksQuery.data ?? []).find((x) => x.id === id);
    return t?.unit_number ?? "—";
  };
  const [submitting, setSubmitting] = useState(false);

  // Delete confirmation
  const [deleteTarget, setDeleteTarget] = useState<PriceBookEntry | null>(null);
  const [deleting, setDeleting] = useState(false);

  /* ---- Filtered list ---- */
  const filtered = useMemo(() => {
    const scopeMatch = SCOPES.find((s) => s.key === scope)!.match;
    return (entries ?? []).filter((e) => {
      if (!scopeMatch(e)) return false;
      if (customerFilter !== "all" && e.customer_id !== customerFilter) return false;
      if (typeFilter !== "all" && e.asset_type !== typeFilter) return false;
      return true;
    });
  }, [entries, scope, customerFilter, typeFilter]);

  const scopeCounts = useMemo(
    () => (entries ? (Object.fromEntries(SCOPES.map((s) => [s.key, entries.filter(s.match).length])) as Record<ScopeKey, number>) : null),
    [entries],
  );
  const uniqueTypes = useMemo(
    () => Array.from(new Set((entries ?? []).map((e) => e.asset_type).filter((t): t is string => !!t))).sort(),
    [entries],
  );

  /* ---- Open Add/Edit modal ---- */
  const openAddModal = () => {
    setEditingId(null);
    setEditingEntry(null);
    setForm(blankForm());
    setModalOpen(true);
  };

  // Prefills from what the API sent. `valid_from` / `valid_to` are calendar
  // days; a date input takes only the YYYY-MM-DD part.
  const openEditModal = (entry: PriceBookEntry) => {
    setEditingId(entry.id);
    setEditingEntry(entry);
    setForm({
      customer_id: entry.customer_id ?? "",
      asset_type: entry.asset_type ?? "",
      item_name: entry.item_name ?? "",
      rate: entry.rate == null ? "" : String(entry.rate),
      standby_rate: entry.standby_rate == null ? "" : String(entry.standby_rate),
      mob_fee: entry.mob_fee == null ? "" : String(entry.mob_fee),
      demob_fee: entry.demob_fee == null ? "" : String(entry.demob_fee),
      billing_trigger: entry.billing_trigger ?? "",
      minimum_days: entry.minimum_days == null ? "" : String(entry.minimum_days),
      currency: entry.currency ?? "CAD",
      valid_from: dayOf(entry.valid_from),
      valid_to: dayOf(entry.valid_to),
      notes: entry.notes ?? "",
      haul: entry.rate_type === HAUL_RATE_TYPE,
      fleet_unit_id: entry.fleet_unit_id ?? "",
    });
    setModalOpen(true);
  };

  /* ---- Submit ---- */
  const handleSubmit = async () => {
    const built = priceBookBody(form, editingId ? editingEntry : null);
    if ("error" in built) {
      toast.error(built.error);
      return;
    }

    setSubmitting(true);
    try {
      if (editingId) {
        await api.rentals.priceBooks.update(editingId, built.body);
        toast.success("Rate updated successfully");
      } else {
        await api.rentals.priceBooks.create(built.body);
        toast.success("Rate created successfully");
      }
      setModalOpen(false);
      queryClient.invalidateQueries({ queryKey: ["rentals", "priceBooks", "all"] });
    } catch (err) {
      toast.error(getApiErrorMessage(err, editingId ? "Failed to update rate" : "Failed to create rate"));
    } finally {
      setSubmitting(false);
    }
  };

  /* ---- Delete ---- */
  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.rentals.priceBooks.delete(deleteTarget.id);
      toast.success("Rate deleted successfully");
      setDeleteTarget(null);
      queryClient.invalidateQueries({ queryKey: ["rentals", "priceBooks", "all"] });
    } catch {
      toast.error("Failed to delete rate");
    } finally {
      setDeleting(false);
    }
  };

  // Rows hold Edit and Delete buttons, so they follow Rate Sheets, not Quotes:
  // no button role, and Enter/Space open the row only when the row itself has focus.
  const rowKeys = (entry: PriceBookEntry) => (event: React.KeyboardEvent<HTMLTableRowElement>) => {
    if (event.target !== event.currentTarget) return;
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      openEditModal(entry);
    }
  };

  const tally = (key: ScopeKey) => (failed || !scopeCounts ? "—" : displayValue(scopeCounts[key]));

  return (
    <div data-testid="price-books-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow="Surface rentals"
        title="Price Books"
        onBack={() => navigate(-1)}
        backLabel="Back"
        actions={
          <button type="button" onClick={openAddModal} className="fi-action fi-action--standard fi-action--primary">
            <Plus className="size-4" aria-hidden="true" />
            Add Rate
          </button>
        }
        stats={[
          {
            icon: <BookOpen className="size-4" aria-hidden="true" />,
            label: "Rates",
            sub: "Current entries",
            tone: "neutral",
            loading,
            value: tally("all"),
          },
          {
            icon: <Users className="size-4" aria-hidden="true" />,
            label: "Customer",
            sub: "Customer-specific",
            tone: "info",
            loading,
            value: tally("customer"),
          },
          {
            icon: <Globe className="size-4" aria-hidden="true" />,
            label: "Default",
            sub: "All customers",
            tone: "success",
            loading,
            value: tally("default"),
          },
          {
            icon: <Layers className="size-4" aria-hidden="true" />,
            label: "Asset Types",
            sub: "With a rate",
            tone: "accent",
            loading,
            value: failed || !entries ? "—" : uniqueTypes.length,
          },
        ]}
      />

      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
            <div className="scrollbar-hide flex flex-1 gap-2 overflow-x-auto">
              {SCOPES.map((s) => {
                const active = scope === s.key;
                return (
                  <button
                    key={s.key}
                    type="button"
                    aria-pressed={active}
                    onClick={() => setScope(s.key)}
                    className={`inline-flex flex-shrink-0 items-center gap-1.5 whitespace-nowrap rounded-fi-pill px-4 py-1.5 text-xs font-extrabold transition ${
                      active
                        ? "bg-fi-amber text-fi-amber-ink"
                        : "border border-fieldiq-border bg-fieldiq-card text-fieldiq-muted hover:text-fieldiq-text"
                    }`}
                  >
                    {s.label}
                    {/* No count is claimed while the list is in flight or failed. */}
                    {!loading && !failed && scopeCounts && (
                      <span className={`tabular-nums ${active ? "text-fi-amber-ink" : "text-fieldiq-muted"}`}>
                        {displayValue(scopeCounts[s.key])}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
            <div className="flex flex-col gap-2 sm:flex-row">
              <select
                aria-label="Filter by customer"
                value={customerFilter}
                onChange={(e) => setCustomerFilter(e.target.value)}
                className="rounded-fi-control border border-fieldiq-border bg-fieldiq-card px-3 py-1.5 text-sm text-fieldiq-text focus:border-fi-amber focus:outline-none"
              >
                <option value="all">All customers</option>
                {customers.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
              <select
                aria-label="Filter by asset type"
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                className="rounded-fi-control border border-fieldiq-border bg-fieldiq-card px-3 py-1.5 text-sm text-fieldiq-text focus:border-fi-amber focus:outline-none"
              >
                <option value="all">All asset types</option>
                {uniqueTypes.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>
          </div>

          <section
            aria-labelledby="price-book-register-title"
            className="overflow-hidden rounded-fi-panel border border-fieldiq-border border-l-4 border-l-fi-amber bg-fieldiq-card"
          >
            <header className="border-b border-fieldiq-border px-4 py-4">
              <p className="text-[0.65rem] font-black uppercase tracking-[0.14em] text-fieldiq-muted">Rate register</p>
              <h2 id="price-book-register-title" className="mt-1 text-base font-extrabold tracking-tight text-fieldiq-text">
                Rental rates by asset type
              </h2>
            </header>

            {failed ? (
              <LoadFailureNotice error={queryFailureError(priceBooksQuery)} subject="price book rates" />
            ) : loading ? (
              <div className="space-y-3 p-4" aria-hidden="true">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="animate-pulse rounded-fi-panel bg-fieldiq-dark/60 p-4">
                    <div className="h-4 w-1/3 rounded bg-fieldiq-border" />
                    <div className="mt-2 h-3 w-1/4 rounded bg-fieldiq-border" />
                  </div>
                ))}
              </div>
            ) : entries && entries.length === 0 ? (
              <div className="px-4 py-16 text-center">
                <BookOpen size={40} className="mx-auto mb-3 text-fieldiq-muted" aria-hidden="true" />
                <p className="mb-2 text-lg text-fieldiq-text">No current rates</p>
                <p className="text-sm text-fieldiq-muted">This price book has no rate in effect today.</p>
              </div>
            ) : filtered.length === 0 ? (
              <div className="px-4 py-16 text-center">
                <p className="mb-2 text-lg text-fieldiq-text">No matching rates</p>
                <p className="text-sm text-fieldiq-muted">Try another scope, customer or asset type.</p>
              </div>
            ) : (
              <>
                <div className="hidden overflow-x-auto md:block">
                  <table className="w-full min-w-[1040px] text-left">
                    <thead className="border-b border-fieldiq-border bg-fieldiq-dark/40">
                      <tr className="border-l-4 border-l-transparent text-xs font-bold uppercase tracking-wider text-fieldiq-muted">
                        <th className="px-4 py-3">Asset type</th>
                        <th className="px-4 py-3">Item</th>
                        <th className="px-4 py-3">Customer</th>
                        <th className="px-4 py-3">Rate</th>
                        <th className="px-4 py-3">Standby</th>
                        <th className="px-4 py-3">Billing trigger</th>
                        <th className="px-4 py-3">Min days</th>
                        <th className="px-4 py-3">Valid to</th>
                        <th className="w-[96px] px-4 py-3">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filtered.map((entry) => (
                        <tr
                          key={entry.id}
                          data-testid="price-book-row"
                          tabIndex={0}
                          onClick={() => openEditModal(entry)}
                          onKeyDown={rowKeys(entry)}
                          // A rate has no status, so the rail keeps its width and claims no colour.
                          className="cursor-pointer border-t border-l-4 border-fieldiq-border border-l-transparent outline-none transition-colors hover:bg-fieldiq-dark/50 focus-visible:bg-fi-amber/10"
                        >
                          <td className="px-4 py-3 text-sm font-extrabold text-fieldiq-text">{entry.asset_type ?? "—"}</td>
                          <td className="px-4 py-3 text-sm text-fieldiq-text">
                            {entry.item_name ?? "—"}
                            {entry.rate_type === HAUL_RATE_TYPE && <p data-testid="haul-rate-truck" className="text-[11px] text-fieldiq-muted">Haul · {truckLabel(entry.fleet_unit_id)}</p>}
                          </td>
                          <td className="px-4 py-3 text-sm text-fieldiq-muted">{customerLabel(entry)}</td>
                          <td className="whitespace-nowrap px-4 py-3 text-sm font-bold tabular-nums text-fieldiq-text"><RateCell e={entry} /></td>
                          <td className="px-4 py-3 text-sm tabular-nums text-fieldiq-muted">{fmtCurrency(entry.standby_rate, entry.currency)}</td>
                          <td className="px-4 py-3"><TriggerChip value={entry.billing_trigger} /></td>
                          <td className="px-4 py-3 text-sm tabular-nums text-fieldiq-text">{displayValue(entry.minimum_days)}</td>
                          <td className="px-4 py-3 font-mono text-sm text-fieldiq-muted">{entry.valid_to ? fmtDay(entry.valid_to) : "—"}</td>
                          <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                            <div className="flex items-center gap-1">
                              <button
                                type="button"
                                onClick={() => openEditModal(entry)}
                                aria-label={`Edit ${entry.item_name ?? "rate"}`}
                                className="rounded-fi-control p-1.5 text-fieldiq-muted transition-colors hover:bg-fieldiq-dark hover:text-fieldiq-text"
                              >
                                <Pencil className="size-4" />
                              </button>
                              <button
                                type="button"
                                onClick={() => setDeleteTarget(entry)}
                                aria-label={`Delete ${entry.item_name ?? "rate"}`}
                                className="rounded-fi-control p-1.5 text-fieldiq-muted transition-colors hover:bg-fieldiq-dark hover:text-fieldiq-danger"
                              >
                                <Trash2 className="size-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="space-y-3 p-4 md:hidden">
                  {filtered.map((entry) => (
                    <div key={entry.id} data-testid="price-book-card" className="rounded-fi-panel border border-fieldiq-border bg-fieldiq-card p-4">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="truncate font-extrabold text-fieldiq-text">{entry.item_name ?? "—"}</p>
                          {entry.rate_type === HAUL_RATE_TYPE && <p className="text-xs text-fieldiq-muted">Haul · {truckLabel(entry.fleet_unit_id)}</p>}
                          <p className="text-xs text-fieldiq-muted">
                            {entry.asset_type ?? "—"} · {customerLabel(entry)}
                          </p>
                        </div>
                        <TriggerChip value={entry.billing_trigger} />
                      </div>
                      <dl className="mt-3 grid grid-cols-2 gap-2 text-sm">
                        <div><dt className="text-xs text-fieldiq-muted">Rate</dt><dd className="font-bold tabular-nums text-fieldiq-text"><RateCell e={entry} /></dd></div>
                        <div><dt className="text-xs text-fieldiq-muted">Standby</dt><dd className="tabular-nums text-fieldiq-text">{fmtCurrency(entry.standby_rate, entry.currency)}</dd></div>
                        <div><dt className="text-xs text-fieldiq-muted">Min days</dt><dd className="tabular-nums text-fieldiq-text">{displayValue(entry.minimum_days)}</dd></div>
                        <div><dt className="text-xs text-fieldiq-muted">Valid to</dt><dd className="text-fieldiq-text">{entry.valid_to ? fmtDay(entry.valid_to) : "—"}</dd></div>
                      </dl>
                      <div className="mt-3 flex gap-2">
                        <button type="button" onClick={() => openEditModal(entry)} className="flex items-center gap-1.5 rounded-fi-pill border border-fieldiq-border bg-fieldiq-dark px-3.5 py-1.5 text-sm font-bold text-fieldiq-text">
                          <Pencil size={13} aria-hidden="true" /> Edit
                        </button>
                        <button type="button" onClick={() => setDeleteTarget(entry)} className="flex items-center gap-1.5 rounded-fi-pill border border-fieldiq-border bg-fieldiq-dark px-3.5 py-1.5 text-sm font-bold text-fieldiq-text">
                          <Trash2 size={13} aria-hidden="true" /> Delete
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

                <p className="border-t border-fieldiq-border px-4 py-3 text-xs text-fieldiq-muted">
                  Showing {filtered.length} of {entries?.length ?? 0} current rates. Rates past their valid-to date are not listed.
                </p>
              </>
            )}
          </section>
        </div>
      </section>

      {/* Add / Edit Modal */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="bg-card border-border max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-foreground">
              {editingId ? "Edit Rate" : "Add Rate"}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {/* Customer */}
            <div className="space-y-1.5">
              <Label className="text-muted-foreground text-sm">Customer (optional)</Label>
              <Select value={form.customer_id} onValueChange={(v) => setForm((f) => ({ ...f, customer_id: v === "__default__" ? "" : v }))}>
                <SelectTrigger className="bg-background border-border">
                  <SelectValue placeholder="Default (all customers)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__default__">Default (all customers)</SelectItem>
                  {customers.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Haul rate (api#1054), offered when the company bills hauls by the hour. */}
            {!editingId && billHaul && (
              <div className="space-y-1.5">
                <Label htmlFor="price-book-rate-for" className="text-muted-foreground text-sm">Rate for</Label>
                <select
                  id="price-book-rate-for"
                  className="h-10 w-full rounded-md border border-border bg-background px-3 text-sm text-foreground"
                  value={form.haul ? "haul" : "equipment"}
                  onChange={(e) => {
                    const haul = e.target.value === "haul";
                    setForm((f) => ({ ...f, haul, item_name: haul && !f.item_name ? "Haul" : f.item_name }));
                  }}
                >
                  <option value="equipment">Rental equipment</option>
                  <option value="haul">Haul, by the hour</option>
                </select>
              </div>
            )}

            {form.haul && (
              <div className="space-y-1.5">
                <Label htmlFor="price-book-truck" className="text-muted-foreground text-sm">Truck</Label>
                <select
                  id="price-book-truck"
                  className="h-10 w-full rounded-md border border-border bg-background px-3 text-sm text-foreground"
                  value={form.fleet_unit_id}
                  onChange={(e) => setForm((f) => ({ ...f, fleet_unit_id: e.target.value }))}
                >
                  <option value="">Any truck</option>
                  {form.fleet_unit_id && !activeTrucks.some((t) => t.id === form.fleet_unit_id) && (
                    <option value={form.fleet_unit_id}>{truckLabel(form.fleet_unit_id)}</option>
                  )}
                  {activeTrucks.map((t) => <option key={t.id} value={t.id}>{t.unit_number ?? t.id}</option>)}
                </select>
                {trucksFailed && <p className="text-xs text-foreground">Couldn't load the trucks.</p>}
              </div>
            )}

            {/* Asset Type */}
            {!form.haul && (
            <div className="space-y-1.5">
              <Label className="text-muted-foreground text-sm">Asset Type *</Label>
              <Select value={form.asset_type} onValueChange={(v) => setForm((f) => ({ ...f, asset_type: v }))}>
                <SelectTrigger className="bg-background border-border">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  {ASSET_TYPES.map((t) => (
                    <SelectItem key={t} value={t}>{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            )}

            {/* Item Name */}
            <div className="space-y-1.5">
              <Label className="text-muted-foreground text-sm">Item Name *</Label>
              <Input
                className="bg-background border-border"
                value={form.item_name}
                onChange={(e) => setForm((f) => ({ ...f, item_name: e.target.value }))}
                placeholder="e.g. 500 bbl Production Tank"
              />
            </div>

            {/* Rates row */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="price-book-rate" className="text-muted-foreground text-sm">
                  {rateLabel(form.haul ? HAUL_RATE_TYPE : editingId ? editingEntry?.rate_type ?? null : "daily")} *
                </Label>
                <Input
                  id="price-book-rate"
                  className="bg-background border-border"
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.rate}
                  onChange={(e) => setForm((f) => ({ ...f, rate: e.target.value }))}
                />
              </div>
              {!form.haul && (
              <div className="space-y-1.5">
                <Label htmlFor="price-book-standby" className="text-muted-foreground text-sm">Standby Rate ($)</Label>
                <Input
                  id="price-book-standby"
                  className="bg-background border-border"
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.standby_rate}
                  onChange={(e) => setForm((f) => ({ ...f, standby_rate: e.target.value }))}
                  placeholder="None"
                />
              </div>
              )}
            </div>

            {!form.haul && (<>
            {/* Mob / demob fees (api#953). Blank is no fee; 0 is a $0 fee. */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="price-book-mob" className="text-muted-foreground text-sm">Mob Fee ($)</Label>
                <Input
                  id="price-book-mob"
                  className="bg-background border-border"
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.mob_fee}
                  onChange={(e) => setForm((f) => ({ ...f, mob_fee: e.target.value }))}
                  placeholder="No fee"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="price-book-demob" className="text-muted-foreground text-sm">Demob Fee ($)</Label>
                <Input
                  id="price-book-demob"
                  className="bg-background border-border"
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.demob_fee}
                  onChange={(e) => setForm((f) => ({ ...f, demob_fee: e.target.value }))}
                  placeholder="No fee"
                />
              </div>
            </div>
            <p className="-mt-2 text-xs text-muted-foreground">Blank is no fee; 0 is a $0 fee. A fee here replaces the asset&rsquo;s own.</p>

            {/* Billing Trigger */}
            <div className="space-y-1.5">
              <Label className="text-muted-foreground text-sm">Billing Trigger</Label>
              <Select value={form.billing_trigger} onValueChange={(v) => setForm((f) => ({ ...f, billing_trigger: v }))}>
                <SelectTrigger className="bg-background border-border">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {BILLING_TRIGGERS.map((t) => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            </>)}

            {/* Min Days + Currency */}
            <div className="grid grid-cols-2 gap-3">
              {!form.haul && (
              <div className="space-y-1.5">
                <Label htmlFor="price-book-min-days" className="text-muted-foreground text-sm">Minimum Days</Label>
                <Input
                  id="price-book-min-days"
                  className="bg-background border-border"
                  type="number"
                  min="1"
                  value={form.minimum_days}
                  onChange={(e) => setForm((f) => ({ ...f, minimum_days: e.target.value }))}
                />
              </div>
              )}
              <div className="space-y-1.5">
                <Label className="text-muted-foreground text-sm">Currency</Label>
                <Select value={form.currency} onValueChange={(v) => setForm((f) => ({ ...f, currency: v }))}>
                  <SelectTrigger className="bg-background border-border">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CURRENCIES.map((c) => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Dates */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-muted-foreground text-sm">Valid From</Label>
                <Input
                  className="bg-background border-border"
                  type="date"
                  value={form.valid_from}
                  onChange={(e) => setForm((f) => ({ ...f, valid_from: e.target.value }))}
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-muted-foreground text-sm">Valid To</Label>
                <Input
                  className="bg-background border-border"
                  type="date"
                  value={form.valid_to}
                  onChange={(e) => setForm((f) => ({ ...f, valid_to: e.target.value }))}
                />
              </div>
            </div>

            {/* Notes */}
            <div className="space-y-1.5">
              <Label className="text-muted-foreground text-sm">Notes</Label>
              <Textarea
                className="bg-background border-border"
                rows={3}
                value={form.notes}
                onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                placeholder="Optional notes..."
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setModalOpen(false)} className="border-border">
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={submitting}
              className="bg-primary hover:bg-primary/90 text-primary-foreground"
            >
              {submitting && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
              {editingId ? "Save Changes" : "Create Rate"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteTarget} onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}>
        <DialogContent className="bg-card border-border max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-foreground">Delete Rate</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground py-2">
            Are you sure you want to delete the rate for{" "}
            <span className="text-foreground font-medium">{deleteTarget?.item_name}</span>? This action cannot be undone.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)} className="border-border">
              Cancel
            </Button>
            <Button
              onClick={handleDelete}
              disabled={deleting}
              className="border border-black/20 bg-fi-signal-red text-fi-signal-ink hover:opacity-90"
            >
              {deleting && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PriceBooks;
